import React, { useRef, useEffect, useState } from 'react';
import { Button } from './ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from './ui/tooltip';
import { Check, ThumbsDown, MessageSquare } from 'lucide-react';
import { RotatingDeleteButton } from './RotatingDeleteButton';
import { TextDiff } from './TextDiff';
import { DisabledTooltip } from './EmptyStateSecondaryButton';
import { cn } from '@/lib/utils';
import {
  COLOR_SUGGESTION_STRENGTH,
  COLOR_SUGGESTION_ORTHOGRAPHIC,
  COLOR_SUGGESTION_STYLE,
  COLOR_SUGGESTION_USER_EDIT,
} from '../colors';
import {
  isDeletionSuggestion as isDeletionSuggestionKind,
  isInsertionSuggestion as isInsertionSuggestionKind,
} from '../utils/editor-utils';
import { InlineMarkdownText } from './InlineMarkdownText';

export function isTemporarySuggestionId(id: string): boolean {
  return id.startsWith('temp-');
}

export type Suggestion = {
  id: string;
  userId: string;
  userEmail?: string;
  documentId: string | null;
  originalText: string;
  suggestionText: string;
  issueType: 'orthographic' | 'preciseness' | 'clarity' | 'consistency' | 'orthographic_consistency' | 'grammar' | 'spelling' | 'punctuation' | 'sentence_structure' | 'repetition' | 'overused_words' | 'cacophony' | 'passive_voice' | 'show_vs_tell' | 'pacing' | 'adverbs' | 'adjectives' | 'cliches' | 'transitions' | 'style' | 'strength' | 'user_edit';
  feedbackText: string;
  llmCallId: string | null;
  chatThreadId?: string | null;
  chatMessageId?: string | null;
  chatQuestion?: string | null;
  llmCall?: {
    id: string;
    userId: string | null;
    documentId: string | null;
    triggerSource: string;
    provider: 'gemini' | 'openai';
    model: string;
    prompt: string;
    response: string | null;
    promptTokens: number | null;
    responseTokens: number | null;
    costUsd: number | null;
    createdAt: string;
  } | null;
  languageCode: string | null;
  languageName: string | null;
  shownAt: string;
  status: 'pending' | 'applied' | 'rejected' | 'reported';
  closedAt: string | null;
  createdAt: string;
  scopeFromPos?: number | null;
  scopeToPos?: number | null;
  scopeSelectedText?: string | null;
  fromPos?: number | null;
  toPos?: number | null;
  fromRel?: Record<string, unknown> | null;
  toRel?: Record<string, unknown> | null;
  contextBefore?: string | null;
  contextAfter?: string | null;
};

type SuggestionCardProps = {
  suggestion: Suggestion;
  onScrollToSuggestion?: (suggestion: Suggestion) => void;
  onApplySuggestion?: (suggestion: Suggestion) => void;
  onRejectSuggestion?: (suggestion: Suggestion) => void;
  onDismissSuggestion?: (suggestion: Suggestion) => void;
  onNavigateToChat?: (threadId: string, messageId: string) => void;
  t: (key: string) => string;
  getIssueTypeLabel: (issueType: string) => string;
  showDivider?: boolean;
  showOriginalAtTop?: boolean;
  isMobileDrawer?: boolean;
};

export const SuggestionCard: React.FC<SuggestionCardProps> = ({
  suggestion,
  onScrollToSuggestion,
  onApplySuggestion,
  onRejectSuggestion,
  onDismissSuggestion,
  onNavigateToChat,
  t,
  getIssueTypeLabel,
  showDivider = false,
  showOriginalAtTop = false,
  isMobileDrawer = false,
}) => {
  const buttonContainerRef = useRef<HTMLDivElement>(null);
  const [isNarrow, setIsNarrow] = useState(false);
  const isTemporarySuggestion = isTemporarySuggestionId(suggestion.id);
  const temporaryActionsTooltip = t('suggestion_actions_unavailable_until_saved');
  const isStrengthFeedback = suggestion.issueType === 'strength';
  const isUserEditSuggestion = suggestion.issueType === 'user_edit';
  const isDeletionSuggestion = isDeletionSuggestionKind(suggestion);
  const isInsertionSuggestion = !isStrengthFeedback && isInsertionSuggestionKind(suggestion);
  const hasReplacement = !isStrengthFeedback && !isInsertionSuggestion && (suggestion.suggestionText ?? '') !== '';
  const issueTypeLabel = isUserEditSuggestion
    ? (suggestion.userEmail || t('suggestion_issue_type_user_edit'))
    : getIssueTypeLabel(suggestion.issueType);
  const rejectTooltip = isTemporarySuggestion
    ? temporaryActionsTooltip
    : suggestion.issueType === 'strength'
      ? t('suggestion_reject_tooltip_strength')
      : t('suggestion_reject_tooltip_regular');
  const applyTooltip = isTemporarySuggestion
    ? temporaryActionsTooltip
    : suggestion.issueType === 'strength'
      ? t('suggestion_apply_tooltip_strength')
      : isDeletionSuggestion
        ? t('suggestion_apply_tooltip_delete')
        : isInsertionSuggestion
          ? t('suggestion_apply_tooltip_insert')
          : t('suggestion_apply_tooltip_regular');
  const dismissTooltip = isTemporarySuggestion
    ? temporaryActionsTooltip
    : suggestion.issueType === 'user_edit'
      ? t('commenter_suggestion_remove_tooltip')
      : suggestion.issueType === 'strength'
        ? t('suggestion_dismiss_tooltip_strength')
        : t('suggestion_dismiss_tooltip_regular');

  useEffect(() => {
    const container = buttonContainerRef.current;
    if (!container) return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const width = entry.contentRect.width;
        setIsNarrow(width < 350);
      }
    });

    resizeObserver.observe(container);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  const canScrollToSuggestion = Boolean(onScrollToSuggestion);

  return (
    <div>
      <div
        className={cn(
          'py-6 px-3 bg-card text-card-foreground text-left',
          canScrollToSuggestion && 'cursor-pointer rounded-md border border-transparent hover:border-border transition-colors'
        )}
        onClick={() => {
          if (onScrollToSuggestion) {
            onScrollToSuggestion(suggestion);
          }
        }}
      >
        <div className="flex items-center gap-2 mb-2">
          <div
            className="w-2 h-2 rounded-full shrink-0"
            style={{
              backgroundColor: suggestion.issueType === 'strength'
                ? COLOR_SUGGESTION_STRENGTH
                : suggestion.issueType === 'orthographic'
                  ? COLOR_SUGGESTION_ORTHOGRAPHIC
                  : suggestion.issueType === 'user_edit'
                    ? COLOR_SUGGESTION_USER_EDIT
                    : COLOR_SUGGESTION_STYLE,
            }}
          />
          <span className={cn('text-xs text-muted-foreground', !isUserEditSuggestion && 'uppercase')}>
            {issueTypeLabel}
          </span>
          {suggestion.chatThreadId && suggestion.chatMessageId && onNavigateToChat && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    className="ml-auto inline-flex items-center justify-center rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
                    aria-label={t('chat_suggestions_source_tooltip')}
                    onClick={(event) => {
                      event.stopPropagation();
                      onNavigateToChat(suggestion.chatThreadId!, suggestion.chatMessageId!);
                    }}
                  >
                    <MessageSquare className="h-3.5 w-3.5" />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="top" className="max-w-xs">
                  <p>{suggestion.chatQuestion || t('chat_suggestions_source_tooltip')}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
          {suggestion.status === 'applied' && (
            <span className="text-xs text-success">{t('applied')}</span>
          )}
          {suggestion.status === 'rejected' && (
            <span className="text-xs text-red-600">{t('rejected')}</span>
          )}
        </div>
        {(isStrengthFeedback || showOriginalAtTop) && !isDeletionSuggestion && suggestion.originalText && (
          <div className="mb-2">
            <div className="text-[0.9rem] font-semibold text-left">
              "<InlineMarkdownText text={suggestion.originalText} />"
            </div>
          </div>
        )}
        {suggestion.feedbackText && !isUserEditSuggestion && (
          <div className="mb-2">
            <div className="text-[0.9rem] text-left">{suggestion.feedbackText}</div>
          </div>
        )}
        {hasReplacement && (
          <TextDiff
            before={suggestion.originalText}
            after={suggestion.suggestionText}
            variant="replacement"
            beforeLabel={t('suggestion_before')}
            afterLabel={t('suggestion_after')}
            deleteLabel={t('suggestion_deletion')}
          />
        )}
        {isInsertionSuggestion && (
          <TextDiff
            before=""
            after={suggestion.suggestionText}
            variant="insertion"
            insertLabel={t('suggestion_insertion')}
            whitespaceLabel={`[${t('suggestion_whitespace')}]`}
          />
        )}
        {isDeletionSuggestion && (
          <TextDiff
            before={suggestion.originalText}
            variant="deletion"
            beforeLabel={t('suggestion_before')}
            afterLabel={t('suggestion_after')}
            deleteLabel={t('suggestion_deletion')}
          />
        )}
        {suggestion.status === 'pending' && (onApplySuggestion || onRejectSuggestion || onDismissSuggestion) && (
          <div
            ref={buttonContainerRef}
            className={isMobileDrawer 
              ? "flex items-center justify-between gap-2 mt-6" 
              : "flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mt-6"
            }
            onClick={(e) => e.stopPropagation()}
          >
            {isMobileDrawer ? (
              <>
                {onRejectSuggestion && (
                  <DisabledTooltip enabled={isTemporarySuggestion} text={temporaryActionsTooltip}>
                    <Button
                      variant="ghost"
                      className="h-10 flex-1 shrink-0"
                      disabled={isTemporarySuggestion}
                      onClick={() => onRejectSuggestion(suggestion)}
                    >
                      <ThumbsDown className="h-4 w-4 text-red-600 shrink-0" />
                    </Button>
                  </DisabledTooltip>
                )}
                {onApplySuggestion && (
                  <DisabledTooltip enabled={isTemporarySuggestion} text={temporaryActionsTooltip}>
                    <Button
                      variant="secondary"
                      size="sm"
                      className="flex-1 shrink-0"
                      disabled={isTemporarySuggestion}
                      onClick={() => onApplySuggestion(suggestion)}
                    >
                      <Check className="h-4 w-4 shrink-0" />
                    </Button>
                  </DisabledTooltip>
                )}
                    {onDismissSuggestion && (
                      <RotatingDeleteButton
                        onClick={() => onDismissSuggestion(suggestion)}
                        disabled={isTemporarySuggestion}
                        tooltipText={dismissTooltip}
                        className="h-10 shrink-0"
                      />
                    )}
              </>
            ) : (
              <TooltipProvider delayDuration={1000}>
                <>
                  {onRejectSuggestion && (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className={isNarrow ? 'inline-flex shrink-0' : 'inline-flex w-full sm:w-auto shrink-0'}>
                          <Button
                            variant="ghost"
                            className={`h-7 ${isNarrow ? 'w-7 p-0 shrink-0' : 'w-full sm:w-auto shrink-0'}`}
                            disabled={isTemporarySuggestion}
                            onClick={() => onRejectSuggestion(suggestion)}
                          >
                            <ThumbsDown className={`h-3 w-3 ${isNarrow ? '' : 'mr-1'} text-red-600 shrink-0`} />
                            {!isNarrow && <span className="whitespace-nowrap">{suggestion.issueType === 'strength' ? t('bad_opinion') : t('bad_suggestion')}</span>}
                          </Button>
                        </span>
                      </TooltipTrigger>
                      <TooltipContent className="bg-[#272729] text-white">
                        <p>{rejectTooltip}</p>
                      </TooltipContent>
                    </Tooltip>
                  )}
                  <div className="flex items-center gap-2 flex-1 sm:flex-initial justify-end min-w-0">
                    {onApplySuggestion && (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className={isNarrow ? 'inline-flex shrink-0' : 'inline-flex flex-1 sm:flex-initial shrink-0'}>
                            <Button
                              variant="secondary"
                              size="sm"
                              className={isNarrow ? 'w-7 p-0 shrink-0' : 'w-full sm:w-auto shrink-0 gap-1'}
                              disabled={isTemporarySuggestion}
                              onClick={() => onApplySuggestion(suggestion)}
                            >
                              <Check className="h-3 w-3 shrink-0" />
                              {!isNarrow && <span className="whitespace-nowrap">{suggestion.issueType === 'strength' ? t('ok') : isDeletionSuggestion ? t('delete_text') : isInsertionSuggestion ? t('insert') : t('replace')}</span>}
                            </Button>
                          </span>
                        </TooltipTrigger>
                        <TooltipContent className="bg-[#272729] text-white">
                          <p>{applyTooltip}</p>
                        </TooltipContent>
                      </Tooltip>
                    )}
                    {onDismissSuggestion && (
                      <RotatingDeleteButton
                        onClick={() => onDismissSuggestion(suggestion)}
                        disabled={isTemporarySuggestion}
                        tooltipText={dismissTooltip}
                        className="h-7 w-7 p-0 shrink-0"
                      />
                    )}
                  </div>
                </>
              </TooltipProvider>
            )}
          </div>
        )}
      </div>
      {showDivider && (
        <div className="border-b border-border" />
      )}
    </div>
  );
};

